<template>
  <div>
    <PageHeader :title="isEdit ? 'Edit Automation' : 'Create Automation'" showBack />

    <b-card class="mb-3">
      <b-form @submit.prevent="handleSave">
        <!-- Basic Info -->
        <b-form-group label="Name">
          <b-form-input v-model="form.name" required placeholder="e.g. Welcome email on new lead" />
        </b-form-group>
        <b-form-group label="Description">
          <b-form-textarea v-model="form.description" rows="2" placeholder="Optional description..." />
        </b-form-group>

        <hr />

        <!-- When -->
        <h5>When</h5>
        <p class="text-muted small">When should this automation run?</p>
        <b-form-group label="When this happens">
          <b-form-select v-model="form.trigger_type" :options="triggerOptions" required @change="onTriggerTypeChange" />
        </b-form-group>

        <!-- Stage Change Config -->
        <template v-if="form.trigger_type === 'stage_change'">
          <b-form-group label="Stage Field">
            <b-form-select v-model="form.trigger_config.stage_field" :options="stageFieldOptions" required @change="onStageFieldChange" />
          </b-form-group>
          <b-form-group label="Stage Value" v-if="form.trigger_config.stage_field">
            <b-form-select v-model="form.trigger_config.stage_value" :options="stageValueOptions" required />
          </b-form-group>
        </template>

        <!-- Tag Config -->
        <template v-if="form.trigger_type === 'tag_added' || form.trigger_type === 'tag_removed'">
          <b-form-group label="Match" v-if="form.trigger_type === 'tag_added'">
            <b-form-select v-model="tagMatch" :options="tagMatchOptions" />
          </b-form-group>
          <b-form-group
            v-if="isPrefixMatch"
            label="Tag starts with"
            description="Fires for any tag that begins with this, e.g. &quot;Medical Assistant - Huntsville&quot; also matches &quot;Medical Assistant - Huntsville - Start 11-02-2026&quot;."
          >
            <b-form-input v-model="form.trigger_config.tag_prefix" required />
          </b-form-group>
          <b-form-group label="Tag" v-else>
            <b-form-select v-model="form.trigger_config.tag_id" :options="tagOptions" required />
          </b-form-group>
        </template>

        <hr />

        <!-- Actions -->
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">Actions</h5>
          <b-button size="sm" variant="outline-primary" @click="addAction">+ Add Action</b-button>
        </div>
        <p class="text-muted small">What should happen?</p>

        <div v-if="form.actions.length === 0" class="text-center text-muted py-3 border rounded mb-3">
          No actions configured. Click "+ Add Action" to get started.
        </div>

        <b-card v-for="(action, index) in form.actions" :key="index" class="mb-2">
          <div class="d-flex justify-content-between align-items-start">
            <span class="badge badge-secondary mr-2">{{ index + 1 }}</span>
            <div class="flex-grow-1">
              <b-form-group label="Action Type" class="mb-2">
                <b-form-select v-model="action.action_type" :options="actionTypeOptions" required @change="onActionTypeChange(action)" />
              </b-form-group>

              <!-- Send Email Config -->
              <template v-if="action.action_type === 'send_email'">
                <b-form-group label="Email Template" class="mb-2">
                  <b-form-select v-model="action.action_config.template_id" :options="emailTemplateOptions" required />
                </b-form-group>
                <b-form-group label="From Email" class="mb-2">
                  <b-form-input v-model="action.action_config.from_email" type="email" required placeholder="noreply@school.com" />
                </b-form-group>
                <b-form-group label="From Name" class="mb-2">
                  <b-form-input v-model="action.action_config.from_name" placeholder="Optional sender name" />
                </b-form-group>
              </template>

              <!-- Send SMS Config -->
              <template v-if="action.action_type === 'send_sms'">
                <b-form-group label="SMS Template" class="mb-2">
                  <b-form-select v-model="action.action_config.template_id" :options="smsTemplateOptions" />
                </b-form-group>
                <b-form-group label="Or Custom Body" class="mb-2" v-if="!action.action_config.template_id">
                  <b-form-textarea v-model="action.action_config.body" rows="2" placeholder="SMS body text..." />
                </b-form-group>
              </template>

              <!-- Send Contract Config -->
              <template v-if="action.action_type === 'send_contract'">
                <b-form-group label="Contract Template" class="mb-2">
                  <b-form-select v-model="action.action_config.template_id" :options="contractTemplateOptions" required />
                </b-form-group>
              </template>

              <!-- Send Survey Config -->
              <template v-if="action.action_type === 'send_survey'">
                <b-form-group label="Survey" class="mb-2">
                  <b-form-select v-model="action.action_config.survey_id" :options="surveyOptions" required />
                </b-form-group>
              </template>

              <!-- Update Stage Config -->
              <template v-if="action.action_type === 'update_stage'">
                <b-form-group label="Stage Field" class="mb-2">
                  <b-form-select v-model="action.action_config.stage_field" :options="stageFieldOptions" required @change="action.action_config.stage_value = null" />
                </b-form-group>
                <b-form-group label="New Value" class="mb-2" v-if="action.action_config.stage_field">
                  <b-form-select v-model="action.action_config.stage_value" :options="getStageOptions(action.action_config.stage_field)" required />
                </b-form-group>
              </template>

              <!-- Add/Remove Tag Config -->
              <template v-if="action.action_type === 'add_tag' || action.action_type === 'remove_tag'">
                <b-form-group label="Tag" class="mb-2">
                  <b-form-select v-model="action.action_config.tag_id" :options="tagOptions" required />
                </b-form-group>
              </template>

              <!-- Create Note Config -->
              <template v-if="action.action_type === 'create_note'">
                <b-form-group label="Note Content" class="mb-2">
                  <b-form-textarea v-model="action.action_config.content" rows="2" required placeholder="Note text..." />
                </b-form-group>
              </template>

              <!-- Delay -->
              <b-form-group label="Delay (minutes)" class="mb-0">
                <b-form-input v-model.number="action.delay_minutes" type="number" min="0" placeholder="0 = immediate" />
              </b-form-group>
            </div>
            <b-button size="sm" variant="outline-danger" class="ml-2" @click="removeAction(index)">
              <i class="material-icons" style="font-size: 16px;">close</i>
            </b-button>
          </div>
        </b-card>

        <hr />

        <div class="d-flex justify-content-between">
          <b-form-checkbox v-model="form.is_active" switch>Active</b-form-checkbox>
          <b-button type="submit" variant="primary" :disabled="isSaving">
            {{ isSaving ? 'Saving...' : (isEdit ? 'Update Automation' : 'Create Automation') }}
          </b-button>
        </div>
      </b-form>
    </b-card>

    <!-- Logs (edit mode only) -->
    <b-card v-if="isEdit" class="mb-3">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">Execution Logs</h5>
        <b-button size="sm" variant="outline-secondary" @click="fetchLogs">Refresh</b-button>
      </div>
      <b-table :items="logs" :fields="logFields" :busy="logsLoading" responsive show-empty small>
        <template #cell(status)="data">
          <span class="badge" :class="logStatusClass(data.item.status)">{{ data.item.status }}</span>
        </template>
        <template #cell(triggered_at)="data">{{ formatDate(data.item.triggered_at) }}</template>
        <template #table-busy><div class="text-center my-2"><b-spinner small /></div></template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { formatDateTime } from '../../common/utils';
import {
  AUTOMATION_TRIGGER_TYPES,
  AUTOMATION_ACTION_TYPES,
  STAGE_FIELDS,
  LEAD_STAGES,
  STUDENT_STAGES,
  EMPLOYER_STAGES,
  OTHER_STAGES,
} from '../../common/constants';
import PageHeader from '../../components/Ui/PageHeader.vue';

const STAGE_OPTIONS_MAP = {
  lead_stage: LEAD_STAGES,
  student_stage: STUDENT_STAGES,
  employer_stage: EMPLOYER_STAGES,
  other_stage: OTHER_STAGES,
};

export default {
  name: 'AutomationEditor',
  components: { PageHeader },
  data() {
    return {
      isEdit: false,
      isSaving: false,
      form: {
        name: '',
        description: '',
        is_active: false,
        trigger_type: null,
        trigger_config: {},
        actions: [],
      },
      tags: [],
      emailTemplates: [],
      smsTemplates: [],
      contractTemplates: [],
      surveys: [],
      logs: [],
      logsLoading: false,
      logFields: [
        { key: 'contact_name', label: 'Contact' },
        { key: 'status', label: 'Status' },
        { key: 'triggered_at', label: 'When' },
        { key: 'error_message', label: 'Error' },
      ],
    };
  },
  computed: {
    triggerOptions() {
      return [{ value: null, text: 'Select when this happens...' }, ...AUTOMATION_TRIGGER_TYPES];
    },
    actionTypeOptions() {
      return [{ value: null, text: 'Select action...' }, ...AUTOMATION_ACTION_TYPES];
    },
    stageFieldOptions() {
      return [{ value: null, text: 'Select stage...' }, ...STAGE_FIELDS];
    },
    stageValueOptions() {
      const field = this.form.trigger_config.stage_field;
      if (!field || !STAGE_OPTIONS_MAP[field]) return [];
      return [{ value: null, text: 'Select value...' }, ...STAGE_OPTIONS_MAP[field]];
    },
    tagOptions() {
      return [{ value: null, text: 'Select tag...' }, ...this.tags.map(t => ({ value: t.id, text: t.name }))];
    },
    tagMatchOptions() {
      return [
        { value: 'exact', text: 'Exactly this tag' },
        { value: 'prefix', text: 'Any tag that starts with...' },
      ];
    },
    isPrefixMatch() {
      return this.form.trigger_type === 'tag_added' && this.form.trigger_config.tag_match === 'prefix';
    },
    tagMatch: {
      get() {
        return this.form.trigger_config.tag_match || 'exact';
      },
      set(value) {
        this.form.trigger_config = value === 'prefix'
          ? { tag_match: 'prefix', tag_prefix: '' }
          : { tag_id: null };
      },
    },
    emailTemplateOptions() {
      return [{ value: null, text: 'Select template...' }, ...this.emailTemplates.map(t => ({ value: t.id, text: t.name }))];
    },
    smsTemplateOptions() {
      return [{ value: null, text: 'Select template...' }, ...this.smsTemplates.map(t => ({ value: t.id, text: t.name }))];
    },
    contractTemplateOptions() {
      return [{ value: null, text: 'Select template...' }, ...this.contractTemplates.map(t => ({ value: t.id, text: t.name }))];
    },
    surveyOptions() {
      return [{ value: null, text: 'Select survey...' }, ...this.surveys.map(s => ({ value: s.id, text: s.title }))];
    },
  },
  methods: {
    ...mapActions('automation', ['getAutomation', 'createAutomation', 'updateAutomation', 'getAutomationLogs']),
    ...mapActions('tag', ['getAllTags']),
    ...mapActions('emailTemplate', ['getAllEmailTemplates']),
    ...mapActions('smsTemplate', ['getAllSMSTemplates']),
    ...mapActions('contractTemplate', ['getAllContractTemplates']),
    ...mapActions('survey', ['getAllSurveys']),
    formatDate: formatDateTime,
    onTriggerTypeChange() {
      this.form.trigger_config = {};
    },
    onStageFieldChange() {
      this.form.trigger_config.stage_value = null;
    },
    onActionTypeChange(action) {
      action.action_config = {};
    },
    getStageOptions(field) {
      if (!field || !STAGE_OPTIONS_MAP[field]) return [];
      return [{ value: null, text: 'Select value...' }, ...STAGE_OPTIONS_MAP[field]];
    },
    addAction() {
      this.form.actions.push({
        action_type: null,
        action_config: {},
        order_num: this.form.actions.length,
        delay_minutes: 0,
      });
    },
    removeAction(index) {
      this.form.actions.splice(index, 1);
      this.form.actions.forEach((a, i) => { a.order_num = i; });
    },
    logStatusClass(status) {
      return {
        'badge-success': status === 'success',
        'badge-danger': status === 'failed',
        'badge-warning': status === 'skipped',
      };
    },
    async fetchLogs() {
      if (!this.$route.params.id) return;
      this.logsLoading = true;
      try {
        const res = await this.getAutomationLogs(this.$route.params.id);
        this.logs = res.data || [];
      } catch (err) { /* ignore */ }
      finally { this.logsLoading = false; }
    },
    async handleSave() {
      this.isSaving = true;
      try {
        const user = this.$store.getters['auth/getLoggedInUser'];
        const payload = { ...this.form, school: user.school.id };

        if (this.isEdit) {
          await this.updateAutomation({ id: this.$route.params.id, data: payload });
          this.makeToast('success', 'Automation updated');
        } else {
          await this.createAutomation(payload);
          this.makeToast('success', 'Automation created');
          this.$router.push({ name: 'automations-list' });
        }
      } catch (err) {
        this.makeToast('danger', 'Failed to save automation');
      } finally {
        this.isSaving = false;
      }
    },
    async loadAutomation() {
      try {
        const res = await this.getAutomation(this.$route.params.id);
        const data = res.data;
        this.form = {
          name: data.name,
          description: data.description || '',
          is_active: data.is_active,
          trigger_type: data.trigger_type,
          trigger_config: data.trigger_config || {},
          actions: (data.actions || []).map(a => ({
            action_type: a.action_type,
            action_config: a.action_config || {},
            order_num: a.order_num,
            delay_minutes: a.delay_minutes || 0,
          })),
        };
      } catch (err) {
        this.makeToast('danger', 'Failed to load automation');
      }
    },
  },
  async created() {
    this.isEdit = !!this.$route.params.id;

    try {
      const [tagRes, emailRes, smsRes, contractTplRes, surveyRes] = await Promise.all([
        this.getAllTags(),
        this.getAllEmailTemplates(),
        this.getAllSMSTemplates(),
        this.getAllContractTemplates(),
        this.getAllSurveys(),
      ]);
      this.tags = tagRes.data.results || tagRes.data || [];
      this.emailTemplates = emailRes.data.results || emailRes.data || [];
      this.smsTemplates = smsRes.data.results || smsRes.data || [];
      this.contractTemplates = contractTplRes.data.results || contractTplRes.data || [];
      this.surveys = surveyRes.data.results || surveyRes.data || [];
    } catch (err) { /* ignore */ }

    if (this.isEdit) {
      await this.loadAutomation();
      this.fetchLogs();
    }
  },
};
</script>
